import React from 'react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { Briefcase } from 'lucide-react';
import './SourceEffectivenessChart.css';

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="source-tooltip">
        <p className="tooltip-source">{data.source}</p>
        <div className="tooltip-stats">
          <div className="tooltip-stat">
            <span className="stat-label">Total Candidates</span>
            <span className="stat-value">{data.total}</span>
          </div>
          <div className="tooltip-stat">
            <span className="stat-label">Hired</span>
            <span className="stat-value hired">{data.hired}</span>
          </div>
          <div className="tooltip-stat">
            <span className="stat-label">Hire Rate</span>
            <span className="stat-value rate">{data.hireRate.toFixed(1)}%</span>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

const SourceSkeleton = () => {
  const barData = [
    { primary: 85, secondary: 35 },
    { primary: 70, secondary: 45 },
    { primary: 55, secondary: 25 },
    { primary: 45, secondary: 30 },
    { primary: 35, secondary: 15 },
    { primary: 25, secondary: 10 }
  ];
  return (
    <div className="source-skeleton">
      <div className="source-skeleton-chart">
        {barData.map((heights, index) => (
          <div key={index} className="source-skeleton-bar-group">
            <div className="source-skeleton-bars">
              <div className="source-skeleton-bar primary" style={{ height: `${heights.primary}%` }} />
              <div className="source-skeleton-bar secondary" style={{ height: `${heights.secondary}%` }} />
            </div>
            <div className="source-skeleton-bar-label" />
          </div>
        ))}
        <div className="source-skeleton-line" />
      </div>
      <div className="source-skeleton-summary">
        <div className="source-skeleton-stat">
          <div className="source-skeleton-stat-label" />
          <div className="source-skeleton-stat-value" />
        </div>
        <div className="source-skeleton-stat">
          <div className="source-skeleton-stat-label" />
          <div className="source-skeleton-stat-value" />
        </div>
        <div className="source-skeleton-stat">
          <div className="source-skeleton-stat-label" />
          <div className="source-skeleton-stat-value" />
        </div>
        <div className="source-skeleton-stat">
          <div className="source-skeleton-stat-label" />
          <div className="source-skeleton-stat-value" />
        </div>
      </div>
    </div>
  );
};

const SourceEffectivenessChart = ({ data, isLoading }) => {
  // Show skeleton while loading
  if (isLoading) {
    return <SourceSkeleton />;
  }

  // Handle nested data structure from API: { sources: [...], totals: {...}, ... }
  const sources = data?.sources || [];

  if (!sources || sources.length === 0) {
    return (
      <div className="source-empty">
        <Briefcase className="source-empty-icon" />
        <p className="source-empty-title">No Source Data Yet</p>
        <p className="source-empty-message">
          Source effectiveness data will appear here once candidates are added with their source information.
        </p>
      </div>
    );
  }

  // Transform and sort by total candidates descending
  const sortedData = sources
    .map(s => ({
      source: s.source,
      total: s.totalCandidates || 0,
      hired: s.hiredCount || 0,
      hireRate: s.hireRate || 0
    }))
    .sort((a, b) => b.total - a.total);

  // Calculate totals
  const totalCandidates = sortedData.reduce((sum, s) => sum + s.total, 0);
  const totalHired = sortedData.reduce((sum, s) => sum + s.hired, 0);
  const overallHireRate = totalCandidates > 0 ? (totalHired / totalCandidates) * 100 : 0;

  // Find best performing source (at least 5 candidates)
  const bestSource = sortedData.reduce((best, current) => {
    if (current.total >= 5 && current.hireRate > (best?.hireRate || 0)) {
      return current;
    }
    return best;
  }, null);

  return (
    <div className="source-chart-container">
      <ResponsiveContainer width="100%" height={350}>
        <ComposedChart
          data={sortedData}
          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="source"
            tick={{ fontSize: 11, fontFamily: 'Fira Sans' }}
            angle={-45}
            textAnchor="end"
            height={80}
            interval={0}
          />
          <YAxis
            yAxisId="left"
            tick={{ fontSize: 12, fontFamily: 'Fira Code' }}
            label={{
              value: 'Candidates',
              angle: -90,
              position: 'insideLeft',
              style: { fontFamily: 'Fira Sans', fontSize: 12 }
            }}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={{ fontSize: 12, fontFamily: 'Fira Code' }}
            domain={[0, 100]}
            label={{
              value: 'Hire Rate %',
              angle: 90,
              position: 'insideRight',
              style: { fontFamily: 'Fira Sans', fontSize: 12 }
            }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ paddingTop: 10, fontFamily: 'Fira Sans', fontSize: 12 }}
          />
          <Bar
            yAxisId="left"
            dataKey="total"
            name="Total Candidates"
            fill="#1E40AF"
            radius={[4, 4, 0, 0]}
            barSize={40}
          />
          <Bar
            yAxisId="left"
            dataKey="hired"
            name="Hired"
            fill="#10B981"
            radius={[4, 4, 0, 0]}
            barSize={40}
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="hireRate"
            name="Hire Rate %"
            stroke="#F59E0B"
            strokeWidth={3}
            dot={{ fill: '#F59E0B', strokeWidth: 2, r: 5 }}
          />
        </ComposedChart>
      </ResponsiveContainer>

      {/* Source summary */}
      <div className="source-summary">
        <div className="source-summary-item">
          <span className="summary-label">Total Candidates</span>
          <span className="summary-value">{totalCandidates}</span>
        </div>
        <div className="source-summary-item">
          <span className="summary-label">Total Hired</span>
          <span className="summary-value hired">{totalHired}</span>
        </div>
        <div className="source-summary-item">
          <span className="summary-label">Overall Hire Rate</span>
          <span className="summary-value rate">{overallHireRate.toFixed(1)}%</span>
        </div>
        {bestSource && (
          <div className="source-summary-item best">
            <span className="summary-label">Best Source</span>
            <span className="summary-value">{bestSource.source} ({bestSource.hireRate.toFixed(1)}%)</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default SourceEffectivenessChart;
